import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import numberFormat from "../../../../../../utils/Utils";
import {
  ACCENT,
  ACCENT_SOFT,
  Field,
  fieldLabelStyle,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";
import { PendingCapitalI } from "../../../../../../api/request/Lead/Interfaces/PolicyInterface";
import PendingCapitalLine from "../pendingCapital/PendingCapitalLine";

interface BeneficiariesFlowProps {
  insuredCapital: number;
  pendingCapital?: PendingCapitalI | null;
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "modality" | "form" | "confirm";
type ModalityT =
  | "FREE_DESIGNATION"
  | "PLEDGE_CLAUSE"
  | "EXCLUSIVE"
  | "LEGAL_HEIRS"
  | "ORDER_OF_PRECEDENCE";

interface PersonForm {
  name: string;
  lastName: string;
  lastName2: string;
  idNumber: string;
  capital: string;
}

interface BankForm {
  bankName: string;
  loanNumber: string;
}

const MAX_FREE = 10;
const MAX_BANKS = 4;
const MAX_PLEDGE_PEOPLE = 6;
const MAX_SUBSIDIARIES = 6;

const emptyPerson = (): PersonForm => ({
  name: "",
  lastName: "",
  lastName2: "",
  idNumber: "",
  capital: "",
});

const emptyBank = (): BankForm => ({ bankName: "", loanNumber: "" });

const personValid = (p: PersonForm, withCapital: boolean): boolean =>
  p.name.trim() !== "" &&
  p.lastName.trim() !== "" &&
  p.idNumber.trim() !== "" &&
  (!withCapital || parseInt(p.capital, 10) > 0);

const personToPayload = (p: PersonForm, withCapital: boolean) => ({
  name: p.name.trim(),
  lastName: p.lastName.trim(),
  lastName2: p.lastName2.trim() !== "" ? p.lastName2.trim() : null,
  idNumber: p.idNumber.trim(),
  ...(withCapital ? { capital: parseInt(p.capital, 10) } : {}),
});

const personLabel = (p: PersonForm) =>
  `${p.name.trim()} ${p.lastName.trim()}${
    p.lastName2.trim() !== "" ? ` ${p.lastName2.trim()}` : ""
  } · ${p.idNumber.trim()}`;

// Module-level on purpose: defining these inside the flow component would give
// them a new identity on every render, remounting the inputs and losing focus
// after each keystroke.
const PersonEditor = ({
  person,
  index,
  withCapital,
  invalid,
  t,
  onChange,
  onRemove,
}: {
  person: PersonForm;
  index: number;
  withCapital: boolean;
  invalid: boolean;
  t: (key: string) => string;
  onChange: (p: PersonForm) => void;
  onRemove?: () => void;
}) => (
  <div
    style={{
      border: `1px solid ${invalid ? "#fca5a5" : "#e5e7eb"}`,
      borderRadius: 10,
      padding: "14px 16px",
      marginBottom: 12,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
        {t("beneficiaryN").replace("{n}", String(index + 1))}
      </span>
      {onRemove && (
        <button
          onClick={onRemove}
          style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
          aria-label={t("remove")}
        >
          <GlobalIcon iconName="TrashIcon" size="XS" color="#9ca3af" />
        </button>
      )}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <input
        style={inputStyle}
        placeholder={t("personName")}
        value={person.name}
        onChange={(e) => onChange({ ...person, name: e.target.value })}
      />
      <input
        style={inputStyle}
        placeholder={t("personLastName")}
        value={person.lastName}
        onChange={(e) => onChange({ ...person, lastName: e.target.value })}
      />
      <input
        style={inputStyle}
        placeholder={t("personLastName2")}
        value={person.lastName2}
        onChange={(e) => onChange({ ...person, lastName2: e.target.value })}
      />
      <input
        style={inputStyle}
        placeholder={t("personId")}
        value={person.idNumber}
        onChange={(e) => onChange({ ...person, idNumber: e.target.value })}
      />
      {withCapital && (
        <input
          style={{ ...inputStyle, gridColumn: "1 / -1" }}
          placeholder={t("personCapital")}
          inputMode="numeric"
          value={person.capital}
          onChange={(e) =>
            onChange({ ...person, capital: e.target.value.replace(/\D/g, "") })
          }
        />
      )}
    </div>
  </div>
);

const AddButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%",
      border: `1px dashed ${ACCENT}`,
      backgroundColor: "transparent",
      color: ACCENT,
      borderRadius: 10,
      padding: "10px 0",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      marginBottom: 16,
    }}
  >
    + {label}
  </button>
);

const BeneficiariesFlow = ({
  insuredCapital,
  pendingCapital,
  onClose,
  onSubmitted,
}: BeneficiariesFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("modality");
  const [modality, setModality] = useState<ModalityT | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [people, setPeople] = useState<PersonForm[]>([emptyPerson()]);
  const [banks, setBanks] = useState<BankForm[]>([emptyBank()]);
  const [principal, setPrincipal] = useState<PersonForm>(emptyPerson());
  const [subsidiaryMode, setSubsidiaryMode] = useState<"EQUAL_PARTS" | "ORDERED">(
    "EQUAL_PARTS"
  );
  const [subsidiaries, setSubsidiaries] = useState<PersonForm[]>([emptyPerson()]);

  const t = (key: string) =>
    translate(`newDashboard.modifications.beneficiaries.${key}`);

  const modalities: { key: ModalityT; needsInput: boolean }[] = [
    { key: "FREE_DESIGNATION", needsInput: true },
    { key: "PLEDGE_CLAUSE", needsInput: true },
    { key: "EXCLUSIVE", needsInput: true },
    { key: "LEGAL_HEIRS", needsInput: false },
    { key: "ORDER_OF_PRECEDENCE", needsInput: false },
  ];

  const assignedCapital = people.reduce(
    (sum, p) => sum + (parseInt(p.capital, 10) || 0),
    0
  );
  const remainingCapital = insuredCapital - assignedCapital;

  const formValid = (): boolean => {
    if (modality === "FREE_DESIGNATION") {
      return (
        people.length >= 1 &&
        people.length <= MAX_FREE &&
        people.every((p) => personValid(p, true)) &&
        remainingCapital === 0
      );
    }
    if (modality === "PLEDGE_CLAUSE") {
      return (
        banks.length >= 1 &&
        banks.length <= MAX_BANKS &&
        banks.every((b) => b.bankName.trim() !== "" && b.loanNumber.trim() !== "") &&
        people.length >= 1 &&
        people.length <= MAX_PLEDGE_PEOPLE &&
        people.every((p) => personValid(p, false))
      );
    }
    if (modality === "EXCLUSIVE") {
      return (
        personValid(principal, false) &&
        subsidiaries.length >= 1 &&
        subsidiaries.length <= MAX_SUBSIDIARIES &&
        subsidiaries.every((p) => personValid(p, false))
      );
    }
    return true;
  };

  const buildPayload = (): Record<string, unknown> => {
    if (modality === "FREE_DESIGNATION") {
      return {
        modality,
        beneficiaries: people.map((p) => personToPayload(p, true)),
      };
    }
    if (modality === "PLEDGE_CLAUSE") {
      return {
        modality,
        banks: banks.map((b) => ({
          bankName: b.bankName.trim(),
          loanNumber: b.loanNumber.trim(),
        })),
        remainderBeneficiaries: people.map((p) => personToPayload(p, false)),
      };
    }
    if (modality === "EXCLUSIVE") {
      return {
        modality,
        principal: personToPayload(principal, false),
        subsidiaryMode,
        subsidiaries: subsidiaries.map((p) => personToPayload(p, false)),
      };
    }
    return { modality };
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.postModificationRequest("BENEFICIARIES_CHANGE", buildPayload());
      onSubmitted();
      setNotification({
        message: translate("newDashboard.modifications.common.successToast"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      const key =
        error?.status === 409
          ? "newDashboard.modifications.errors.alreadyInProgress"
          : "newDashboard.modifications.errors.generic";
      setNotification({ message: translate(key), type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  const requestAdvice = async () => {
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.postBeneficiariesAdvice();
      setNotification({ message: t("adviceSent"), type: "success", time: 5 });
    } catch (error) {
      setNotification({
        message: translate("newDashboard.modifications.errors.generic"),
        type: "error",
        time: 5,
      });
    }
  };

  const selectModality = (m: ModalityT) => {
    setModality(m);
    setShowErrors(false);
    setStep(modalities.find((x) => x.key === m)?.needsInput ? "form" : "confirm");
  };

  const updateAt = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, i) => (i === index ? value : item));

  const removeAt = <T,>(list: T[], index: number): T[] =>
    list.filter((_, i) => i !== index);

  // ── Steps ────────────────────────────────────────────────────────────────
  return (
    <ModificationModal onClose={onClose} maxWidth={620}>
      {step === "modality" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {modalities.map((m) => (
              <button
                key={m.key}
                onClick={() => selectModality(m.key)}
                style={{
                  textAlign: "left",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  backgroundColor: "#fff",
                  padding: "14px 16px",
                  cursor: "pointer",
                }}
              >
                <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                  {t(`modality.${m.key}.title`)}
                </span>
                <span style={{ display: "block", fontSize: 13, color: "#64748b", marginTop: 4 }}>
                  {t(`modality.${m.key}.desc`)}
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={requestAdvice}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: ACCENT,
              fontSize: 14,
              fontWeight: 600,
              padding: 0,
              textAlign: "center",
              width: "100%",
            }}
          >
            {t("adviceCta")}
          </button>
        </div>
      )}

      {step === "form" && modality === "FREE_DESIGNATION" && (
        <div>
          <h2 style={modalTitleStyle}>{t("modality.FREE_DESIGNATION.title")}</h2>
          <p style={modalDescStyle}>{t("freeDesc")}</p>
          <div
            style={{
              ...summaryBoxStyle,
              backgroundColor: remainingCapital < 0 ? "#fef2f2" : ACCENT_SOFT,
              borderColor: remainingCapital < 0 ? "#fca5a5" : "transparent",
            }}
          >
            <SummaryRow
              label={t("insuredCapital")}
              value={numberFormat(insuredCapital, "capital")}
            />
            <PendingCapitalLine
              pendingCapital={pendingCapital}
              current={insuredCapital}
              style={{ textAlign: "right", margin: "-4px 0 8px" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 14,
                padding: "4px 0",
                color: remainingCapital < 0 ? "#dc2626" : "#111827",
                fontWeight: 700,
              }}
            >
              <span>{remainingCapital < 0 ? t("capitalExceeded") : t("remainingCapital")}</span>
              <span>{numberFormat(Math.abs(remainingCapital), "capital")}</span>
            </div>
          </div>
          {people.map((p, i) => (
            <PersonEditor
              key={i}
              person={p}
              index={i}
              withCapital
              invalid={showErrors && !personValid(p, true)}
              t={t}
              onChange={(v) => setPeople(updateAt(people, i, v))}
              onRemove={people.length > 1 ? () => setPeople(removeAt(people, i)) : undefined}
            />
          ))}
          {people.length < MAX_FREE && (
            <AddButton
              onClick={() => setPeople([...people, emptyPerson()])}
              label={t("addBeneficiary")}
            />
          )}
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid()) return setShowErrors(true);
              setStep("confirm");
            }}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("modality")}
          />
        </div>
      )}

      {step === "form" && modality === "PLEDGE_CLAUSE" && (
        <div>
          <h2 style={modalTitleStyle}>{t("modality.PLEDGE_CLAUSE.title")}</h2>
          <p style={modalDescStyle}>{t("pledgeDesc")}</p>
          <p style={fieldLabelStyle}>{t("banksTitle")}</p>
          {banks.map((b, i) => (
            <div
              key={i}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                  {t("bankN").replace("{n}", String(i + 1))}
                </span>
                {banks.length > 1 && (
                  <button
                    onClick={() => setBanks(removeAt(banks, i))}
                    style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
                    aria-label={t("remove")}
                  >
                    <GlobalIcon iconName="TrashIcon" size="XS" color="#9ca3af" />
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input
                  style={inputStyle}
                  placeholder={t("bankName")}
                  value={b.bankName}
                  onChange={(e) =>
                    setBanks(updateAt(banks, i, { ...b, bankName: e.target.value }))
                  }
                />
                <input
                  style={inputStyle}
                  placeholder={t("loanNumber")}
                  value={b.loanNumber}
                  onChange={(e) =>
                    setBanks(updateAt(banks, i, { ...b, loanNumber: e.target.value }))
                  }
                />
              </div>
            </div>
          ))}
          {banks.length < MAX_BANKS && (
            <AddButton onClick={() => setBanks([...banks, emptyBank()])} label={t("addBank")} />
          )}
          <p style={fieldLabelStyle}>{t("remainderTitle")}</p>
          {people.map((p, i) => (
            <PersonEditor
              key={i}
              person={p}
              index={i}
              withCapital={false}
              invalid={showErrors && !personValid(p, false)}
              t={t}
              onChange={(v) => setPeople(updateAt(people, i, v))}
              onRemove={people.length > 1 ? () => setPeople(removeAt(people, i)) : undefined}
            />
          ))}
          {people.length < MAX_PLEDGE_PEOPLE && (
            <AddButton
              onClick={() => setPeople([...people, emptyPerson()])}
              label={t("addBeneficiary")}
            />
          )}
          {people.length > 1 && (
            <p style={{ ...modalDescStyle, marginTop: -4 }}>{t("pledgeEqualSplitNote")}</p>
          )}
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid()) return setShowErrors(true);
              setStep("confirm");
            }}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("modality")}
          />
        </div>
      )}

      {step === "form" && modality === "EXCLUSIVE" && (
        <div>
          <h2 style={modalTitleStyle}>{t("modality.EXCLUSIVE.title")}</h2>
          <p style={modalDescStyle}>{t("exclusiveDesc")}</p>
          <p style={fieldLabelStyle}>{t("principalTitle")}</p>
          <PersonEditor
            person={principal}
            index={0}
            withCapital={false}
            invalid={showErrors && !personValid(principal, false)}
            t={t}
            onChange={setPrincipal}
          />
          <p style={fieldLabelStyle}>{t("subsidiaryModeTitle")}</p>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            {(["EQUAL_PARTS", "ORDERED"] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSubsidiaryMode(mode)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  border: `2px solid ${subsidiaryMode === mode ? ACCENT : "#e5e7eb"}`,
                  backgroundColor: subsidiaryMode === mode ? ACCENT_SOFT : "#fff",
                  color: "#1e293b",
                }}
              >
                {t(`subsidiaryMode.${mode}`)}
              </button>
            ))}
          </div>
          <p style={fieldLabelStyle}>{t("subsidiariesTitle")}</p>
          {subsidiaries.map((p, i) => (
            <PersonEditor
              key={i}
              person={p}
              index={i}
              withCapital={false}
              invalid={showErrors && !personValid(p, false)}
              t={t}
              onChange={(v) => setSubsidiaries(updateAt(subsidiaries, i, v))}
              onRemove={
                subsidiaries.length > 1
                  ? () => setSubsidiaries(removeAt(subsidiaries, i))
                  : undefined
              }
            />
          ))}
          {subsidiaries.length < MAX_SUBSIDIARIES && (
            <AddButton
              onClick={() => setSubsidiaries([...subsidiaries, emptyPerson()])}
              label={t("addBeneficiary")}
            />
          )}
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid()) return setShowErrors(true);
              setStep("confirm");
            }}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("modality")}
          />
        </div>
      )}

      {step === "confirm" && modality && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>
            {translate("newDashboard.modifications.common.confirmSignatureDesc")}
          </p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("summaryModality")} value={t(`modality.${modality}.title`)} />
            {modality === "FREE_DESIGNATION" &&
              people.map((p, i) => (
                <SummaryRow
                  key={i}
                  label={personLabel(p)}
                  value={numberFormat(parseInt(p.capital, 10) || 0, "capital")}
                />
              ))}
            {modality === "PLEDGE_CLAUSE" && (
              <>
                {banks.map((b, i) => (
                  <SummaryRow
                    key={`b${i}`}
                    label={t("bankN").replace("{n}", String(i + 1))}
                    value={`${b.bankName.trim()} · ${b.loanNumber.trim()}`}
                  />
                ))}
                {people.map((p, i) => (
                  <SummaryRow key={`p${i}`} label={t("remainderShort")} value={personLabel(p)} />
                ))}
              </>
            )}
            {modality === "EXCLUSIVE" && (
              <>
                <SummaryRow label={t("principalShort")} value={personLabel(principal)} />
                <SummaryRow
                  label={t("subsidiaryModeTitle")}
                  value={t(`subsidiaryMode.${subsidiaryMode}`)}
                />
                {subsidiaries.map((p, i) => (
                  <SummaryRow
                    key={i}
                    label={`${i + 1}º`}
                    value={personLabel(p)}
                  />
                ))}
              </>
            )}
            {(modality === "LEGAL_HEIRS" || modality === "ORDER_OF_PRECEDENCE") && (
              <p style={{ fontSize: 14, color: "#374151", whiteSpace: "pre-line", margin: 0 }}>
                {t(`fixedText.${modality}`)}
              </p>
            )}
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirmAndSign")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() =>
              setStep(
                modalities.find((x) => x.key === modality)?.needsInput ? "form" : "modality"
              )
            }
          />
        </div>
      )}

    </ModificationModal>
  );
};

export default BeneficiariesFlow;
